// СЕРВИС НУМЕРАЦИИ - единственное место, где вычисляются номера (как их вычислит writer/builder.py):
//   разделы «1», «1.2», «1.2.3»; в приложениях «А.1»; структурные элементы - без номера;
//   рисунки, таблицы, формулы (нумеруемые), листинги - сквозные, в приложениях «А.1» (счётчики сбрасываются);
//   буквы приложений - по порядку без Ё, З, Й, О, Ч, Ъ, Ы, Ь (явная буква продолжает последовательность);
//   раздел об ИИ (метаданные) - виртуальный раздел перед первым из «Выводы/Заключение/Список источников/приложение»;
//   источники - по алфавиту (иностранные в конце, 4.13.4) или в порядке библиотеки; сноски - по порядку.
// Используется отображением (номера заголовков, подписи, живые ссылки, панели) и сверяется с экспортом тестом
// tests/web/test_numbering_contract.py. Работает и с узлами ProseMirror, и с JSON (адаптеры внизу).
import { APP_LETTERS, STRUCT_KINDS, NBSP, EN_DASH, plainSpaces } from './schema/constants';
import type { Meta, Source } from './types';

export type NumKind = 'sec' | 'struct' | 'app' | 'fig' | 'tbl' | 'eq' | 'lst';

export interface NumInfo {
  kind: NumKind;
  /** номер без оформления: '1.2', 'А', '3', 'А.1' (у структурных элементов - '') */
  num: string;
  /** подпись: 'Рисунок 3', 'Таблица А.1', 'Листинг 2', '(4)', 'Приложение Б', '1.2' */
  label: string;
  /** текст живой ссылки: '3', '(4)', 'Б', '1.2', 'Введение' */
  ref: string;
  /** текст заголовка/подписи (для панелей и выбора ссылки) */
  title: string;
  /** уровень заголовка (1–3) для sec/struct/app */
  level?: number;
  /** буква и статус приложения */
  letter?: string;
  status?: string;
}

export interface Numbering {
  byId: Map<string, NumInfo>;
  /** id в порядке документа (только нумеруемые/ссылочные объекты) */
  order: string[];
  /** id источника -> номер в списке */
  sources: Map<string, number>;
  /** источники в порядке списка: [id, строка] */
  sourceList: [string, string][];
  /** id сноски -> номер */
  footnotes: Map<string, number>;
  counts: { sections: number; figures: number; tables: number; equations: number; listings: number; appendices: number; footnotes: number };
  version: number;
}

/** Минимальный интерфейс узла для обхода (адаптеры: pmAccess, jsonAccess). */
export interface Access<N> {
  type(n: N): string;
  attrs(n: N): Record<string, any>;
  children(n: N): N[];
  text(n: N): string;
}

let version = 0;

export function computeNumbering<N>(doc: N, A: Access<N>, meta: Partial<Meta> | null, sources: Source[]): Numbering {
  const byId = new Map<string, NumInfo>();
  const order: string[] = [];
  const footnotes = new Map<string, number>();
  const counts = { sections: 0, figures: 0, tables: 0, equations: 0, listings: 0, appendices: 0, footnotes: 0 };
  let secN = 0;
  let appIdx = 0;
  let cnt = { fig: 0, tbl: 0, eq: 0, lst: 0 };
  let prefix = '';
  let base: string | null = null;
  let h2 = 0;
  let h3 = 0;
  let aiPending = !!meta?.ai_usage?.enabled;

  const put = (id: string | null | undefined, info: NumInfo) => {
    if (!id) return;
    byId.set(id, info);
    order.push(id);
  };
  const insertAi = () => {
    if (!aiPending) return;
    aiPending = false;
    secN += 1;
    counts.sections += 1;
    put('meta_ai', { kind: 'sec', num: String(secN), label: String(secN), ref: String(secN), level: 1,
      title: 'Информация об использовании технологий искусственного интеллекта (ИИ)' });
  };

  const visitInline = (n: N) => {
    const t = A.type(n);
    if (t === 'footnote') {
      const id = A.attrs(n).id;
      counts.footnotes += 1;
      if (id) footnotes.set(id, counts.footnotes);
      return;
    }
    for (const c of A.children(n)) visitInline(c);
  };

  const heading = (n: N, inAppendix: boolean) => {
    const a = A.attrs(n);
    const title = A.text(n).trim();
    if (!title) return;                               // пустой заголовок не экспортируется - и не нумеруется
    let level = Number(a.level) || 1;
    if (inAppendix) level = Math.max(2, level);
    if (level === 1) {
      secN += 1;
      counts.sections += 1;
      base = String(secN);
      h2 = 0; h3 = 0;
      put(a.id, { kind: 'sec', num: base, label: base, ref: base, level: 1, title });
      return;
    }
    if (base === null) {                             // вне раздела (структурный элемент, начало документа)
      put(a.id, { kind: 'sec', num: '', label: '', ref: title, level, title });
      return;
    }
    let num: string;
    if (level === 2) { h2 += 1; h3 = 0; num = `${base}.${h2}`; } else { h3 += 1; num = `${base}.${h2}.${h3}`; }
    put(a.id, { kind: 'sec', num, label: num, ref: num, level, title });
  };

  const object = <M,>(n: M, B: Access<M>) => {
    const t = B.type(n);
    const a = B.attrs(n);
    const capNode = B.children(n).find((c) => B.type(c) === 'caption');
    const title = capNode ? B.text(capNode).trim() : '';
    if (t === 'figure') {
      // рисунок без файла (рамка для загрузки, составной с пустыми частями) в отчёте есть - рамкой, с номером
      cnt.fig += 1; counts.figures += 1;
      const num = prefix + cnt.fig;
      put(a.id, { kind: 'fig', num, label: `Рисунок${NBSP}${num}`, ref: num, title });
    } else if (t === 'stoTable') {
      cnt.tbl += 1; counts.tables += 1;
      const num = prefix + cnt.tbl;
      put(a.id, { kind: 'tbl', num, label: `Таблица${NBSP}${num}`, ref: num, title });
    } else if (t === 'listing') {
      cnt.lst += 1; counts.listings += 1;
      const num = prefix + cnt.lst;
      put(a.id, { kind: 'lst', num, label: `Листинг${NBSP}${num}`, ref: num, title });
    } else if (t === 'formula') {
      if (!String(a.latex || '').trim()) return;     // пустая формула не экспортируется
      if (a.numbered === false) return;
      cnt.eq += 1; counts.equations += 1;
      const num = prefix + cnt.eq;
      put(a.id, { kind: 'eq', num, label: `(${num})`, ref: `(${num})`, title: String(a.latex || '') });
    }
  };

  const block = (n: N, inAppendix: boolean) => {
    const t = A.type(n);
    if (t === 'heading') { heading(n, inAppendix); visitInline(n); return; }
    if (t === 'figure' || t === 'stoTable' || t === 'listing' || t === 'formula') object(n, A);
    visitInline(n);
  };

  // листы перед текстом (writer: «Задание», «Реферат»/«Аннотация» - первыми): их рисунки, таблицы, формулы и
  // листинги входят в сквозную нумерацию раньше текста (как в исходниках: «Рисунок 1» задания, «Рисунок 2» текста).
  // «Задание на курсовую работу/для…/по…» - тоже: сайт выводит его текстом, а свои номера 1, 2… давали в документе
  // два «Рисунка 1» (writer/builder.prepass - так же; sto_check начинает счёт текста после номеров задания)
  for (const key of ['zadanie', 'referat', 'annotation'] as const) {
    const sheet = (meta as any)?.[key];
    if (!sheet?.enabled || !sheet.doc) continue;
    for (const c of jsonAccess.children(sheet.doc as JSONContent)) {
      const t = jsonAccess.type(c);
      if (t === 'figure' || t === 'stoTable' || t === 'listing' || t === 'formula') object(c, jsonAccess);
    }
  }

  for (const n of A.children(doc)) {
    const t = A.type(n);
    const a = A.attrs(n);
    if (t === 'appendix' || (t === 'structHeading' && ['conclusions', 'conclusion', 'sources'].includes(a.kind))) insertAi();
    if (t === 'structHeading') {
      base = null;
      const title = STRUCT_KINDS[a.kind] || a.kind;
      put(a.id, { kind: 'struct', num: '', label: '', ref: title, level: 1, title });
      continue;
    }
    if (t === 'appendix') {
      let letter = String(a.letter || '').trim().toUpperCase();
      if (!letter || !APP_LETTERS.includes(letter)) letter = appIdx < APP_LETTERS.length ? APP_LETTERS[appIdx] : String(appIdx + 1);
      appIdx = APP_LETTERS.includes(letter) ? APP_LETTERS.indexOf(letter) + 1 : appIdx + 1;
      counts.appendices += 1;
      prefix = letter + '.';
      cnt = { fig: 0, tbl: 0, eq: 0, lst: 0 };
      base = letter; h2 = 0; h3 = 0;
      const kids = A.children(n);
      const titleNode = kids.find((c) => A.type(c) === 'appendixTitle');
      put(a.id, { kind: 'app', num: letter, label: `Приложение${NBSP}${letter}`, ref: letter, level: 1,
        title: titleNode ? A.text(titleNode).trim() : '', letter, status: a.status || 'обязательное' });
      for (const c of kids) {
        if (A.type(c) === 'appendixTitle') { visitInline(c); continue; }
        block(c, true);
      }
      continue;
    }
    block(n, false);
  }
  insertAi();

  // источники (порядок - как writer/builder.build_sources)
  const rows: [string, string][] = sources.map((s) => [s.id, (s.text || s.formatted || s.title || s.id || '').trim()]);
  if ((meta?.options?.sources_order || 'alpha') === 'alpha') {
    const key = (t: string): [number, string] => {
      const k = t.replace(/^[^\p{L}\p{N}_]+/u, '').toLowerCase().replace(/ё/g, 'е');
      return [/^[a-z]/.test(k) ? 1 : 0, k];
    };
    rows.sort((x, y) => {
      const a = key(x[1]); const b = key(y[1]);
      if (a[0] !== b[0]) return a[0] - b[0];
      return a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0;   // по кодам символов, как sorted() в Python
    });
  }
  const srcMap = new Map<string, number>();
  rows.forEach(([id], i) => srcMap.set(id, i + 1));
  version += 1;
  return { byId, order, sources: srcMap, sourceList: rows, footnotes, counts, version };
}

/** Текст ссылки на источники, как writer/builder.cite_text: «[1]», «[1; 3]», «[2–4]», «[1, с. 5]». */
export function citeText(items: { source: string; loc?: string }[], nb: Numbering): string {
  // место в источнике - как writer: неразрывные пробелы только при USE_NBSP (иначе и старые U+00A0 - обычными)
  const plain = items.map((it) => [nb.sources.get(it.source), plainSpaces((it.loc || '').trim())] as const);
  if (!plain.length || plain.some(([n]) => n === undefined)) return '[?]';
  if (plain.every(([, loc]) => !loc)) {
    const nums = [...new Set(plain.map(([n]) => n as number))].sort((a, b) => a - b);
    const parts: string[] = [];
    let i = 0;
    while (i < nums.length) {
      let j = i;
      while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
      if (j - i >= 2) parts.push(`${nums[i]}${EN_DASH}${nums[j]}`);
      else for (let k = i; k <= j; k++) parts.push(String(nums[k]));
      i = j + 1;
    }
    return '[' + parts.join(';' + NBSP) + ']';
  }
  // 4.14.5: номера по возрастанию, повторы убираются, три и более подряд без страниц - через тире (как writer.cite_text)
  const seen = new Set<string>();
  const sorted = plain.filter(([n, loc]) => { const k = `${n}|${loc}`; if (seen.has(k)) return false; seen.add(k); return true; })
    .sort((a, b) => (a[0] as number) - (b[0] as number) || (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0));
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    const [n, loc] = sorted[i];
    if (loc) { parts.push(`${n}, ${loc}`); i++; continue; }
    let j = i;
    while (j + 1 < sorted.length && !sorted[j + 1][1] && (sorted[j + 1][0] as number) === (sorted[j][0] as number) + 1) j++;
    if (j - i >= 2) parts.push(`${n}${EN_DASH}${sorted[j][0]}`);
    else for (let k = i; k <= j; k++) parts.push(String(sorted[k][0]));
    i = j + 1;
  }
  return '[' + parts.join(';' + NBSP) + ']';
}

// ------------------------------------------------------------------------------------------------ адаптеры
import type { Node as PMNode } from '@tiptap/pm/model';
import type { JSONContent } from '@tiptap/core';

export const pmAccess: Access<PMNode> = {
  type: (n) => n.type.name,
  attrs: (n) => n.attrs,
  children: (n) => { const out: PMNode[] = []; n.forEach((c) => out.push(c)); return out; },
  text: (n) => n.textContent,
};

const jsonText = (n: JSONContent): string => (n.type === 'text' ? n.text || '' : (n.content || []).map(jsonText).join(''));
export const jsonAccess: Access<JSONContent> = {
  type: (n) => n.type || '',
  attrs: (n) => n.attrs || {},
  children: (n) => n.content || [],
  text: jsonText,
};

export const emptyNumbering = (): Numbering => ({
  byId: new Map(), order: [], sources: new Map(), sourceList: [], footnotes: new Map(),
  counts: { sections: 0, figures: 0, tables: 0, equations: 0, listings: 0, appendices: 0, footnotes: 0 }, version: 0,
});
